import { PlusOutlined } from '@ant-design/icons'
import { useQueries } from '@tanstack/react-query'
import { Button, Card, Flex, Table } from 'antd'
import { FormInstance } from 'antd/lib'
import { ColumnsType } from 'antd/lib/table'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'

import {
  useInvestigationsServiceGetListSpecimensEndpoint,
  useInvestigationsServiceGetSpecimenEndpointKey,
  useTreatmentServiceGetConsultationEndpoint
} from 'src/api/queries'
import { InvestigationsService, SpecimenItemDto } from 'src/api/requests'
import { Paragraph } from 'src/components'
import { TestStatusTag } from 'src/features/tests/components'
import { dayMonthYearHourMinutesFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'

import { InvestigationTestModal } from '../PersonInvestigations'

import { InvestigationDemandModal } from './InvestigationDemandModal'

type Props = {
  isVisible: boolean
  form: FormInstance
  /** Incremented to open the TB investigation request (from the TB screening). */
  openTbRequestSignal?: number
  /** Investigation categories ticked in that request (the recommended TB tests). */
  tbPreselectCategoryIds?: string[]
}

type LabInvestigationBlockProps = {
  title: string
  isTb?: boolean
  data: SpecimenItemDto[]
  isLoading: boolean
  columns: ColumnsType<SpecimenItemDto>
  hideAdd?: boolean
  onSuccessCreate: (id: string, investigationId: string, isTb: boolean) => void
  openSignal?: number
  preselectCategoryIds?: string[]
}

const LabInvestigationBlock = ({
  title,
  isTb,
  data,
  isLoading,
  columns,
  hideAdd,
  onSuccessCreate,
  openSignal,
  preselectCategoryIds
}: LabInvestigationBlockProps) => {
  const { t } = useLocales()
  const [isOpen, setIsOpen] = useState(false)
  // Preselection applies only when the modal was opened from outside.
  const [openedFromSignal, setOpenedFromSignal] = useState(false)
  const blockRef = useRef<HTMLDivElement>(null)

  const onToggle = () => {
    setOpenedFromSignal(false)
    setIsOpen((prev) => !prev)
  }

  // Opened from outside (TB screening "request TB investigations").
  useEffect(() => {
    if (!openSignal) return

    blockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setOpenedFromSignal(true)
    setIsOpen(true)
  }, [openSignal])

  return (
    <div ref={blockRef}>
      <Card loading={isLoading} title={title}>
        {data.length ? (
          <Flex vertical gap={24}>
            <Flex justify="end">
              {!hideAdd && (
                <Button onClick={onToggle} type="primary">
                  {t('consultations.investigation_request')}
                </Button>
              )}
            </Flex>

            <Table
              rowKey="id"
              bordered
              columns={columns}
              scroll={{ x: 'max-content' }}
              dataSource={data}
              pagination={false}
            />
          </Flex>
        ) : (
          <Flex justify="center">
            <Button icon={<PlusOutlined />} onClick={onToggle}>
              {t('consultations.investigation_request')}
            </Button>
          </Flex>
        )}

        {isOpen && (
          <InvestigationDemandModal
            isTb={isTb}
            preselectCategoryIds={
              openedFromSignal ? preselectCategoryIds : undefined
            }
            onSuccess={(id, investigationId) =>
              onSuccessCreate(id, investigationId, !!isTb)
            }
            onCancel={onToggle}
          />
        )}
      </Card>
    </div>
  )
}

export const ConsultationLaboratoryCard = ({
  form,
  isVisible,
  openTbRequestSignal,
  tbPreselectCategoryIds
}: Props) => {
  const { t, currentLang } = useLocales()

  const [createdSpecimens, setCreatedSpecimens] = useState<
    { id: string; isTb: boolean }[]
  >([])
  const [selectedSpecimenId, setSelectedSpecimenId] = useState<string>()

  const latestConsultationId = form.getFieldValue('latestConsultationId')

  const { data: consultationData } = useTreatmentServiceGetConsultationEndpoint(
    { id: String(latestConsultationId) },
    [latestConsultationId],
    { enabled: !!latestConsultationId }
  )

  const { data: specimens, isLoading: isSpecimensLoading } =
    useInvestigationsServiceGetListSpecimensEndpoint(
      { demandIds: consultationData?.investigationDemandsIds },
      [consultationData?.investigationDemandsIds],
      { enabled: (consultationData?.investigationDemandsIds?.length ?? 0) > 0 }
    )

  const createdSpecimenQueries = useQueries({
    queries: createdSpecimens.map(({ id }) => ({
      queryKey: [useInvestigationsServiceGetSpecimenEndpointKey, id],
      queryFn: () => InvestigationsService.getSpecimenEndpoint(id),
      enabled: !!id
    }))
  })

  const onCloseTestsModal = () => {
    setSelectedSpecimenId(undefined)
  }

  const onSuccessCreate = (
    id: string,
    investigationId: string,
    isTb: boolean
  ) => {
    if (id) {
      setCreatedSpecimens((prev) =>
        prev.some((specimen) => specimen.id === id)
          ? prev
          : [...prev, { id, isTb }]
      )
    }

    const investigationDemandIds =
      form.getFieldValue('investigationDemandIds') ?? []

    form.setFieldValue('investigationDemandIds', [
      ...investigationDemandIds,
      investigationId
    ])
  }

  useEffect(() => {
    if (specimens?.data && specimens?.data.length > 0) {
      const investigationDemandIds = specimens.data.map((demand) => demand.id)

      form.setFieldsValue({
        investigationDemandIds,
        hasLaboratory: true
      })
    }
  }, [specimens])

  const columns: ColumnsType<SpecimenItemDto> = [
    {
      key: 'investigation number',
      title: t('investigations.investigation_number'),
      dataIndex: 'code'
    },
    {
      key: 'created date',
      title: t('investigations.created_date'),
      render: ({ createdAt }) =>
        dayjs.utc(createdAt).tz().format(dayMonthYearHourMinutesFormatWithDot)
    },
    {
      key: 'harvest date',
      title: t('investigations.harvest_date'),
      render: ({ harvestDate }) =>
        harvestDate
          ? dayjs
              .utc(harvestDate)
              .tz()
              .format(dayMonthYearHourMinutesFormatWithDot)
          : '-'
    },
    {
      key: 'specimen type',
      title: t('consultations.specimen_type'),
      render: (_, { type }) => type?.translation?.[currentLang.value] ?? '-'
    },
    {
      key: 'status',
      title: t('form.status'),
      render: (_, { status }) => <TestStatusTag status={status} />
    },
    {
      key: 'test type',
      title: t('tests_results.type'),
      render: (_, { demand }) => {
        const text = (
          <div>
            {demand?.categories
              ? demand?.categories?.map((item) => (
                  <div key={item.id}>
                    {item.translation?.[currentLang.value]}
                  </div>
                ))
              : '-'}
          </div>
        )

        return (
          <Paragraph
            ellipsis={{
              rows: 2,
              tooltip: text
            }}
          >
            {text}
          </Paragraph>
        )
      }
    },
    {
      key: 'test_results',
      title: t('investigations.tests'),
      render: (_, { id }) => (
        <Button
          type="link"
          onClick={() => {
            setSelectedSpecimenId(id)
          }}
        >
          {t('common.details')}
        </Button>
      )
    }
  ]

  // Tag each freshly-created specimen with the block (TB / non-TB) it was
  // created from, rather than trusting the refetched specimen's isTb flag.
  const createdSpecimenData: SpecimenItemDto[] = createdSpecimenQueries
    .map((query, index) =>
      query.data
        ? ({
            ...query.data,
            isTb: createdSpecimens[index].isTb
          } as SpecimenItemDto)
        : null
    )
    .filter((specimen): specimen is SpecimenItemDto => specimen !== null)

  const allSpecimens: SpecimenItemDto[] = [
    ...createdSpecimenData,
    ...(specimens?.data || [])
  ]

  const nonTbSpecimens = allSpecimens.filter((specimen) => !specimen.isTb)
  const tbSpecimens = allSpecimens.filter((specimen) => !!specimen.isTb)

  const isLoading =
    createdSpecimenQueries.some((query) => query.isLoading) ||
    isSpecimensLoading

  // Only the TB block allows adding multiple investigations. The non-TB block
  // keeps the original single-creation behavior: its add button is hidden once
  // a non-TB investigation has been created this session.
  const hasCreatedNonTb = createdSpecimens.some((specimen) => !specimen.isTb)

  return (
    <>
      {isVisible && (
        <Card>
          <Flex vertical gap={24}>
            <LabInvestigationBlock
              title={t('organizational_structure.lab')}
              isTb={false}
              data={nonTbSpecimens}
              isLoading={isLoading}
              columns={columns}
              hideAdd={hasCreatedNonTb}
              onSuccessCreate={onSuccessCreate}
            />

            <LabInvestigationBlock
              title={t('consultations.tb_investigations')}
              isTb
              openSignal={openTbRequestSignal}
              preselectCategoryIds={tbPreselectCategoryIds}
              data={tbSpecimens}
              isLoading={isLoading}
              columns={columns}
              onSuccessCreate={onSuccessCreate}
            />
          </Flex>

          {selectedSpecimenId && (
            <InvestigationTestModal
              specimenIds={[selectedSpecimenId]}
              onCancel={onCloseTestsModal}
            />
          )}
        </Card>
      )}
    </>
  )
}
